import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Home.css';

function Home() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/companies`);
        let data = response.data;

        // Calculate rank_score and sort
        data.forEach(company => {
          const rating = company.average_rating || 0;
          const reviews = company.review_count || 0;
          const documents = company.document_count || 0;
          const activity = company.last_active ? new Date(company.last_active).getTime() : 0;
          company.rank_score = rating * 10 + reviews + documents * 2 + activity / 1e13;
        });

        data.sort((a, b) => b.rank_score - a.rank_score);
        setCompanies(data);
      } catch (err) {
        console.error(err);
        setError('Failed to load companies.');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const hasVisited = localStorage.getItem('askme_first_visit');
    if (!hasVisited) {
      setShowWelcome(true);
      localStorage.setItem('askme_first_visit', 'true');
      // Hide toast automatically after 5 seconds
      const timer = setTimeout(() => setShowWelcome(false), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCompanyClick = (handle) => {
    if (!handle) {
      console.warn('Missing handle for company, cannot navigate.');
      return;
    }
    navigate(`/chat/${handle}`);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setSearching(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/search_companies`, {
        params: { query: searchTerm }
      });
      setSearchResults(response.data);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const renderCompanyCard = (company) => (
    <div
      key={company.id}
      className="company-card"
      onClick={() => handleCompanyClick(company.handle)}
      tabIndex={0}
      role="button"
      onKeyPress={(e) => { if (e.key === 'Enter') handleCompanyClick(company.handle); }}
    >
      <div className="company-logo">
        {company.logo_url ? (
          <img src={company.logo_url} alt={`${company.name} logo`} />
        ) : (
          <span>No Logo</span>
        )}
      </div>
      <div className="company-info">
        <h3>{company.name}</h3>
        <div className="company-rating">
          {company.average_rating != null && company.review_count != null ? (
            <p>⭐ {company.average_rating.toFixed(1)} ({company.review_count} reviews)</p>
          ) : (
            <p className="no-rating">No reviews yet</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="home-container">
      {/* Welcome Toast */}
      {showWelcome && (
        <div className="welcome-toast">
          <span>
            👋 Welcome to QueryBox! Select a company to start chatting with their AI assistant.
          </span>
          <button
            className="toast-close-btn"
            onClick={() => setShowWelcome(false)}
            aria-label="Close Welcome Toast"
          >
            ✖
          </button>
        </div>
      )}
  
      <h1 className="home-title">Explore Companies</h1>

      <div className="search-container">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search by service (e.g., insurance, loans)"
          aria-label="Search companies by service"
        />
        <button onClick={handleSearch} disabled={searching}>Search</button>
        {searchTerm && (
          <button className="clear-btn" onClick={handleClearSearch}>
            Clear
          </button>
        )}
      </div>

      {searching && <p className="loading-text">Searching companies...</p>}

      {searchResults.length > 0 && (
        <>
          <h2>Search Results</h2>
          <div className="company-grid">
            {searchResults.map(renderCompanyCard)}
          </div>
        </>
      )}

      {!searchTerm && !searching && (
        <>
          <h2>All Companies</h2>
          {loading ? (
            <p className="loading-text">Loading companies...</p>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : companies.length === 0 ? (
            <p>No companies available.</p>
          ) : (
            <div className="company-grid">
              {companies.map(renderCompanyCard)}
            </div>
          )}
        </>
      )}
      
    </div>
  );
}

export default Home;

